import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import InputAdornment from '@material-ui/core/InputAdornment';

import { providerPageWidth } from '../../constant';

import {
    GoBackButton,
    Text,
    StyledCard,
    StyledCardContent,
    HorizontalRule,
    ContainedButton,
    Input,
    PhoneFormat,
    InputWithEndAdornment,
    OutlinedButton,
    IconButton,
    CrossIcon,
    StyledFormHelperText,
} from '../../components/index';

import { GET_HOSPITAL, topUpInquiry } from '../../services/provider';
import { validEmail, validPhone } from '../../utils/regex';
import { alertError, alertSuccess } from '../../redux/stateActions';

const Checkout = ({ history }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [errors, setErrors] = useState([]);

    const [loading, setLoading] = useState(false);

    const [inputValue, setInputValue] = useState({
        businessName: '',
        businessTAXID: '',
        businessAddress: '',
        businessPhone: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        products: [{ credit: 0 }],
    });

    const totalCredits = useMemo(() => {
        let sum = inputValue.products.reduce((acc, item) => acc + item.credit, 0);
        return sum || 0;
    }, [inputValue]);

    const handleChange = (name, value, index) => {
        const newInputValue = { ...inputValue };

        if (name === 'credit') {
            if (value < 0) return;
            newInputValue['products'][index]['credit'] = parseInt(value, 10);

            setErrors(
                errors.filter(
                    (error) =>
                        error.property !== 'products' && error.property !== `product-${index}`
                )
            );
        } else {
            newInputValue[name] = value;

            setErrors(errors.filter((error) => error.property !== name));
        }
        setInputValue(newInputValue);
    };

    /*--------------- Hospital Detail ---------------*/
    const {
        data: getHospitalData,
        error: getHospitalError,
        loading: getHospitalLoading,
    } = useQuery(GET_HOSPITAL, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getHospitalError) {
            dispatch(alertError(`Cannot get Counseling Service: ${getHospitalError.message}`));
        }
        if (getHospitalData) {
            const { name, phoneNumber, address, id } = getHospitalData.hospital;
            setInputValue({
                ...inputValue,
                hospitalID: id || '',
                businessName: name || '',
                businessTAXID: '',
                businessAddress: address || '',
                businessPhone: phoneNumber || '',
                contactName: user.fullname || '',
                contactEmail: user.email || '',
                contactPhone: user.mobileNumber || '',
            });
        }
    }, [getHospitalError, getHospitalData]);

    const handleAddProduct = () => {
        const newInputValue = { ...inputValue };
        newInputValue['products'].push({ credit: 0 });
        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== 'products'));
    };

    const handleRemoveProduct = (index) => {
        const newInputValue = { ...inputValue };
        newInputValue['products'].splice(index, 1);
        setInputValue(newInputValue);

        setErrors(
            errors.filter(
                (error) => error.property !== 'products' && error.property !== `product-${index}`
            )
        );
    };

    const handleCheckOut = async () => {
        const newErrors = [];

        const {
            businessName,
            businessTAXID,
            businessAddress,
            businessPhone,
            contactName,
            contactEmail,
            contactPhone,
            products,
        } = inputValue;

        if (businessName === '') {
            newErrors.push({
                property: 'businessName',
                message: 'Please provide Name of Bussiness.',
            });
        }

        if (businessTAXID === '') {
            newErrors.push({
                property: 'businessTAXID',
                message: 'Please provide TAX ID.',
            });
        }

        if (businessAddress === '') {
            newErrors.push({
                property: 'businessAddress',
                message: 'Please provide Address',
            });
        }

        if (!validPhone.test(businessPhone)) {
            newErrors.push({
                property: 'businessPhone',
                message: 'Please provide Busssiness Contact number',
            });
        }

        if (contactName === '') {
            newErrors.push({
                property: 'contactName',
                message: 'Please provide contact name.',
            });
        }

        if (!validEmail.test(contactEmail)) {
            newErrors.push({
                property: 'contactEmail',
                message: 'Please provide a valid contact Email.',
            });
        }

        if (!validPhone.test(contactPhone)) {
            newErrors.push({
                property: 'contactPhone',
                message: 'Please provide contact phone number',
            });
        }

        if (_.isEmpty(products)) {
            newErrors.push({
                property: 'products',
                message: 'Must be at least 1 item',
            });
        } else {
            products.forEach((product, index) => {
                if (product.credit === '' || product.credit === 0) {
                    newErrors.push({
                        property: `product-${index}`,
                        message: 'Must be at least 1',
                    });
                }
            });
        }

        if (!_.isEmpty(newErrors)) return setErrors(newErrors);

        const variables = {
            staffID: user.staff.id,
            hospitalID: user.staff.hospital.id,
            businessName,
            businessTAXID,
            businessAddress,
            businessPhone,
            contactName,
            contactEmail,
            contactPhone,
            products,
        };

        try {
            setLoading(true);

            const res = await topUpInquiry(variables);

            if (res.status === 200) {
                dispatch(alertSuccess(`Inquiry sent`));

                handleBackToPaymentHistory();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Cannot send inquiry: ${error.message}`));
        }
    };

    const handleBackToPaymentHistory = () => {
        history.push('/provider/credits');
    };

    return (
        <div
            style={{
                padding: '41px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                position: 'relative',
            }}
        >
            <GoBackButton $text onClick={history.goBack} />

            <Text $fontSize="24px" $bold style={{ marginTop: '20px' }}>
                Invoice Details
            </Text>
            <div
                style={{
                    marginTop: '20px',
                    display: 'grid',
                    gridTemplateColumns: 'auto 320px',
                    columnGap: '20px',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                    <StyledCard>
                        <StyledCardContent
                            style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}
                        >
                            <div
                                style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                            >
                                <Text $fontSize="18px" $bold>
                                    Billing Address
                                </Text>
                                <div style={{ display: 'flex', columnGap: '10px' }}>
                                    <Input
                                        label="Name of Bussiness"
                                        placeholder="Name of Bussiness"
                                        name="businessName"
                                        value={inputValue.businessName}
                                        required
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        helperText={
                                            errors.find(
                                                (value) => value.property === 'businessName'
                                            )?.message
                                        }
                                        disabled={loading || getHospitalLoading}
                                        max={100}
                                    />
                                    <Input
                                        label="TAX ID"
                                        placeholder="089-000-0000"
                                        name="businessTAXID"
                                        value={inputValue.businessTAXID}
                                        required
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        helperText={
                                            errors.find(
                                                (value) => value.property === 'businessTAXID'
                                            )?.message
                                        }
                                        disabled={loading || getHospitalLoading}
                                        max={30}
                                    />
                                </div>
                                <Input
                                    label="Address"
                                    placeholder="000 Cinnamon Rd. Appletart Bangkok 10000"
                                    name="businessAddress"
                                    value={inputValue.businessAddress}
                                    required
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    helperText={
                                        errors.find((value) => value.property === 'businessAddress')
                                            ?.message
                                    }
                                    disabled={loading || getHospitalLoading}
                                    max={200}
                                />
                                <Input
                                    label="Busssiness Contact number"
                                    placeholder="089-000-0000"
                                    name="businessPhone"
                                    value={inputValue.businessPhone}
                                    required
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    $inputMaxWidth="50%"
                                    InputProps={{
                                        inputComponent: PhoneFormat,
                                    }}
                                    helperText={
                                        errors.find((value) => value.property === 'businessPhone')
                                            ?.message
                                    }
                                    disabled={loading || getHospitalLoading}
                                />
                            </div>

                            <div
                                style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                            >
                                <Text $fontSize="18px" $bold>
                                    Contact Person
                                </Text>
                                <Input
                                    label="Name"
                                    placeholder="Name"
                                    name="contactName"
                                    value={inputValue.contactName}
                                    required
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    helperText={
                                        errors.find((value) => value.property === 'contactName')
                                            ?.message
                                    }
                                    disabled={loading || getHospitalLoading}
                                    max={100}
                                />
                                <div style={{ display: 'flex', columnGap: '10px' }}>
                                    <Input
                                        id="email"
                                        label="Contact mail"
                                        placeholder="Contact mail"
                                        name="contactEmail"
                                        type="email"
                                        value={inputValue.contactEmail}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        required
                                        helperText={
                                            errors.find(
                                                (error) => error.property === 'contactEmail'
                                            )?.message
                                        }
                                        disabled={loading || getHospitalLoading}
                                    />
                                    <Input
                                        label="Phone Number"
                                        placeholder="089-000-0000"
                                        name="contactPhone"
                                        value={inputValue.contactPhone}
                                        required
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{
                                            inputComponent: PhoneFormat,
                                        }}
                                        helperText={
                                            errors.find(
                                                (value) => value.property === 'contactPhone'
                                            )?.message
                                        }
                                        disabled={loading || getHospitalLoading}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        rowGap: '10px',
                                        padding: '10px 20px',
                                        border: '1px solid #D2D6E9',
                                        borderRadius: '16px',
                                    }}
                                >
                                    {inputValue.products.map((product, index) => (
                                        <div key={`products-index-` + index}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-end',
                                                    columnGap: '5px',
                                                }}
                                            >
                                                <Input
                                                    label="Credit Quantity"
                                                    placeholder="10000"
                                                    name="credit"
                                                    type="number"
                                                    value={Number(product.credit).toString()}
                                                    required
                                                    onChange={(e) =>
                                                        handleChange(
                                                            e.target.name,
                                                            e.target.value,
                                                            index
                                                        )
                                                    }
                                                    disabled={loading || getHospitalLoading}
                                                    onInput={(e) => {
                                                        e.target.value = Math.max(
                                                            0,
                                                            parseInt(e.target.value)
                                                        )
                                                            .toString()
                                                            .slice(0, 11);
                                                    }}
                                                />
                                                <InputWithEndAdornment
                                                    label="Credit Quantity"
                                                    placeholder="10000"
                                                    name="credit"
                                                    type="number"
                                                    required
                                                    value={Number(product.credit).toString()}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            e.target.name,
                                                            e.target.value,
                                                            index
                                                        )
                                                    }
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            THB
                                                        </InputAdornment>
                                                    }
                                                    disabled={loading || getHospitalLoading}
                                                    onInput={(e) => {
                                                        e.target.value = Math.max(
                                                            0,
                                                            parseInt(e.target.value)
                                                        )
                                                            .toString()
                                                            .slice(0, 11);
                                                    }}
                                                />

                                                {
                                                    <IconButton
                                                        onClick={() => handleRemoveProduct(index)}
                                                        style={{ marginBottom: '3px' }}
                                                        disabled={loading || getHospitalLoading}
                                                    >
                                                        <CrossIcon fill="black" />
                                                    </IconButton>
                                                }
                                            </div>
                                            {errors.find(
                                                (value) => value.property === `product-${index}`
                                            ) && (
                                                <StyledFormHelperText error>
                                                    {
                                                        errors.find(
                                                            (value) =>
                                                                value.property ===
                                                                `product-${index}`
                                                        )?.message
                                                    }
                                                </StyledFormHelperText>
                                            )}
                                        </div>
                                    ))}
                                    {errors.find((value) => value.property === 'products') && (
                                        <StyledFormHelperText error>
                                            {
                                                errors.find(
                                                    (value) => value.property === 'products'
                                                )?.message
                                            }
                                        </StyledFormHelperText>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <OutlinedButton
                                            $color="gray2"
                                            $width="max-content"
                                            onClick={handleAddProduct}
                                            disabled={loading || getHospitalLoading}
                                        >
                                            Add items
                                        </OutlinedButton>
                                    </div>
                                </div>
                            </div>
                        </StyledCardContent>
                    </StyledCard>
                </div>
                <StyledCard style={{ height: 'max-content' }}>
                    <StyledCardContent>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Text $fontSize="18px" $bold>
                                Summary
                            </Text>
                            {/* <FilledIconButton
                                style={{ padding: '0 0 2px 0' }}
                                disabled={loading || getHospitalLoading}
                            >
                                <DownloadIcon2 width="20" height="20" />
                            </FilledIconButton> */}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '30px',
                                rowGap: '15px',
                            }}
                        >
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text>Credits</Text>
                                    <Text>{`${totalCredits} Credits`}</Text>
                                </div>
                            </div>

                            <HorizontalRule />

                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Cost</Text>
                                <Text>{`THB ${totalCredits.toLocaleString('en-US')}`}</Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Vat 7%</Text>
                                <Text>{`THB ${(totalCredits * 0.07).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}`}</Text>
                            </div>

                            <HorizontalRule />

                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text $fontSize="20px" $bold>
                                    Total
                                </Text>
                                <Text $fontSize="20px" $bold>{`THB ${(
                                    totalCredits +
                                    totalCredits * 0.07
                                ).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}`}</Text>
                            </div>

                            <HorizontalRule $color="black" $height="3px" />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: '40px',
                                rowGap: '10px',
                            }}
                        >
                            <ContainedButton
                                $width="100%"
                                $height="48px"
                                onClick={handleCheckOut}
                                disabled={getHospitalLoading}
                                loading={loading || getHospitalLoading}
                            >
                                Send inquiry
                            </ContainedButton>
                        </div>
                    </StyledCardContent>
                </StyledCard>
            </div>
        </div>
    );
};

Checkout.propTypes = {
    history: PropTypes.object,
};

export default Checkout;
